import type { AppEvent, EntryKind, SessionMode } from './events.js';
import type { StudyDate } from './time.js';

/**
 * THE TIMER BOUNDARY.
 *
 * A running session is ephemeral, mutable, high-frequency state — start,
 * pause, resume, tick, tick, tick. None of it belongs in the event log:
 * ticks would write thousands of events a day and wreck every property the
 * log has. Spec §27 says this about the network; it is just as true locally.
 *
 * So the timer lives in ONE small mutable record, device-local, never synced,
 * and emits exactly one event when it ends.
 *
 * The critical detail is that elapsed time is DERIVED from `startedAt`, never
 * accumulated by an interval. A PWA tab gets backgrounded, throttled and
 * killed by Android at will; a student who loses a 50-minute session to a
 * memory reclaim is a student who uninstalls. Deriving from wall-clock makes
 * a session survive a full app kill.
 */
export type TimerState = {
  sessionId: string;
  track: string;
  kind: EntryKind;
  mode: SessionMode;
  planMinutes: number;
  /**
   * What this block is FOR, written before it starts (§48 #7).
   *
   * A session with no stated intention becomes tab-switching with a timer
   * running. Asking costs one line at the only moment the student actually
   * knows the answer, and it is the same field the entry carries afterwards —
   * so the ledger later says "ch.4 problems", not "Physics, 45m".
   */
  intention?: string;
  /** Wall clock, UTC ms, of the most recent start or resume. */
  startedAt: number;
  /** Milliseconds banked by previous run segments. */
  accumulatedMs: number;
  /** Set while paused; elapsed stops advancing. */
  pausedAt: number | null;
};

export function startTimer(input: {
  sessionId: string;
  track: string;
  kind?: EntryKind;
  mode: SessionMode;
  planMinutes: number;
  intention?: string;
  now: number;
}): TimerState {
  const intention = input.intention?.trim();
  return {
    sessionId: input.sessionId,
    track: input.track,
    kind: input.kind ?? 'learn',
    mode: input.mode,
    planMinutes: input.planMinutes,
    startedAt: input.now,
    accumulatedMs: 0,
    pausedAt: null,
    ...(intention ? { intention } : {}),
  };
}

export function elapsedMs(timer: TimerState, now: number): number {
  if (timer.pausedAt !== null) return timer.accumulatedMs;
  return timer.accumulatedMs + Math.max(0, now - timer.startedAt);
}

export function pauseTimer(timer: TimerState, now: number): TimerState {
  if (timer.pausedAt !== null) return timer;
  return {
    ...timer,
    accumulatedMs: elapsedMs(timer, now),
    pausedAt: now,
  };
}

export function resumeTimer(timer: TimerState, now: number): TimerState {
  if (timer.pausedAt === null) return timer;
  return { ...timer, startedAt: now, pausedAt: null };
}

/** Whole minutes, floored — 59 seconds of study is not a minute of credit. */
export function elapsedMinutes(timer: TimerState, now: number): number {
  return Math.floor(elapsedMs(timer, now) / 60_000);
}

export function isComplete(timer: TimerState, now: number): boolean {
  return elapsedMinutes(timer, now) >= timer.planMinutes;
}

type EmitContext = {
  eventId: string;
  /** Id for the entry this produces. Distinct from the event id so a later
   *  correction can point at the entry rather than at the event that made it. */
  entryId: string;
  now: number;
  tzOffsetMinutes: number;
  studyDate: StudyDate;
};

/**
 * The single event a finished session produces — an ordinary entry.
 *
 * A finished timer is not a different kind of fact from an hour typed in by
 * hand; it is the same fact, better measured. `mode: 'timed'` is the only
 * thing that distinguishes it, and that flag is what earns the session bonus
 * and lets the entry be listed, corrected and deleted like any other.
 */
export function completeSession(
  timer: TimerState,
  ctx: EmitContext,
  /**
   * Written on the way out (§48 #7).
   *
   * `nextStep` is the re-entry point — the sentence that makes tomorrow's
   * session start in ten seconds instead of ten minutes of "where was I".
   * `recall` is retrieval practice actually performed rather than recommended:
   * naming what you remember, unprompted, is the single cheapest way to turn
   * a session into something that survives the week.
   *
   * Both optional. A student who closes the phone the moment the bell goes
   * still gets their minutes — the prompts must never gate the record.
   */
  wrap: { nextStep?: string; recall?: string } = {},
): AppEvent {
  const nextStep = wrap.nextStep?.trim();
  const recall = wrap.recall?.trim();

  return {
    id: ctx.eventId,
    type: 'entry.logged',
    ts: ctx.now,
    tzOffsetMinutes: ctx.tzOffsetMinutes,
    studyDate: ctx.studyDate,
    entryId: ctx.entryId,
    track: timer.track,
    minutes: elapsedMinutes(timer, ctx.now),
    kind: timer.kind,
    mode: 'timed',
    sessionId: timer.sessionId,
    planMinutes: timer.planMinutes,
    timerMode: timer.mode,
    ...(timer.intention ? { intention: timer.intention } : {}),
    ...(nextStep ? { nextStep } : {}),
    ...(recall ? { recall } : {}),
  };
}

/**
 * Abandoning still records what happened — honest history matters (§17) — but
 * it earns no XP and never counts as activity, so a bailed session can't
 * prop up a streak.
 */
export function abandonSession(timer: TimerState, ctx: EmitContext): AppEvent {
  return {
    id: ctx.eventId,
    type: 'session.abandoned',
    ts: ctx.now,
    tzOffsetMinutes: ctx.tzOffsetMinutes,
    studyDate: ctx.studyDate,
    sessionId: timer.sessionId,
    track: timer.track,
    minutes: elapsedMinutes(timer, ctx.now),
  };
}
